import { Navigate, useRoutes } from 'react-router-dom';
import { useAppSelector } from './store';

import DashboardLayout from './layouts/dashboard';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
import Login from './pages/Login';
import CompanyLogin from './pages/CompanyLogin';
import ResetPassword from './pages/ResetPassword';
import DashboardApp from './pages/Dashboard';
import CompanyDashboard from './pages/CompanyDashboard';
import NotFound from './pages/Page404';

import Profile from './pages/Profile';
import UserProfile from './pages/User/Profile';
// import VerifyWhatsappOtp from './pages/VerifyWhatsappLogin';
import Users from './pages/Users';
import PropertyCategory from './pages/PropertyCategory';
import PropertySubCategory from './pages/PropertySubCategory';
import PropertySubCategoryForm from './pages/PropertySubCategory/Form';
import PropertyFeatureForm from './pages/PropertyFeature/Form/index';
import PropertyFeature from './pages/PropertyFeature';
import LocationManagement from './pages/LocationManagement';
import LocationManagementForm from './pages/LocationManagement/Form';
import AdvertisePackage from './pages/AdvertisePackage';
import PropertyFacility from './pages/PropertyFacility';
import Advertise from './pages/Advertise';
import AdvertiseView from './pages/Advertise/View';
import Settings from './pages/Settings';
import ContactForm from './pages/ContactForm';
import AdvertiseForm from './pages/Advertise/Form';
import CompanyAdvertise from './pages/CompanyAdvertise';
import CompanyAdvertiseForm from './pages/CompanyAdvertise/Form';
import CompanyAdsPerformance from './pages/CompanyAdsPerformance';
import CompanyAdsPerformanceDetails from './pages/CompanyAdsPerformance/Details';
import PropertyFacilityForm from './pages/PropertyFacility/Form/index';
import Notification from './pages/Notification';
import Payment from './pages/Payment';
import PropertySubCategoryView from './pages/PropertySubCategory/View';
import CreditManagement from './pages/CreditManagement';
import PurchaseCredits from './pages/PurchaseCredits';
import PurchaseHistory from './pages/CreditHistory/PurchaseHistory';
import AllocationHistory from './pages/CreditHistory/AllocationHistory';
import CreditUsageHistory from './pages/CreditHistory/CreditUsageHistory';
import ExpirationHistory from './pages/CreditHistory/ExpirationHistory';
import AccountTypeConversionHistory from './pages/History/AccountTypeConversionHistory';

// KPI pages
import AdvertiserPerformance from './pages/Kpi/AdvertiserPerformance';
import WishlistAnalyzer from './pages/WishlistAnalyzer';
import AdvertiserDetail from './pages/Kpi/AdvertiserDetail';
import AgentManagement from './pages/AgentManagement';
import AgentTransferLogs from './pages/AgentTransferLogs';
import LicenseRenewal from './pages/LicenseRenewal';

type Props = {
  ROLES: Array<string>;
  children: any;
  id?: string;
  ids?: Array<string>;
  type?: Array<string>;
  checkAccess?: boolean;
};

const Router = () => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  // const { data: WhatsappLoginOtpData } = useAppSelector(
  //   (state) => state.whatsappLogin
  // );

  // Get last user role from localStorage for redirect
  const getLastUserRole = () => {
    return localStorage.getItem('lastUserRole');
  };

  const ProtectedRouteWrapper = ({ ROLES, children }: Props) => {
    if (ROLES.includes(userRole) === true) {
      return <>{children}</>;
    } else {
      return <></>;
    }
  };

  // Determine redirect path based on lastUserRole
  const getLoginRedirectPath = () => {
    const lastUserRole = getLastUserRole();
    return lastUserRole === 'company' ? '/company-login' : '/login';
  };

  return useRoutes([
    {
      path: '/',
      element: accessToken ? (
        userRole === 'company' ? (
          <DashboardLayout />
        ) : (
          <DashboardLayout />
        )
      ) : (
        <Navigate to={getLoginRedirectPath()} />
      ),
      children: [
        {
          path: '/',
          element: userRole === 'company' ? (
            <Navigate to='/company-dashboard' />
          ) : (
            <DashboardApp />
          ),
        },
        {
          path: 'company-dashboard',
          element:
            userRole === 'company' ? (
              <CompanyDashboard />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'agent-management',
          element:
            userRole === 'company' ? (
              <AgentManagement />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'agent-transfer-logs',
          element:
            userRole === 'company' ? (
              <AgentTransferLogs />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'company-advertise',
          element:
            userRole === 'company' ? (
              <CompanyAdvertise />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'company-advertise/view/:id',
          element:
            userRole === 'company' ? (
              <AdvertiseView />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'company-advertise/create',
          element:
            userRole === 'company' ? (
              <CompanyAdvertiseForm />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'company-advertise/edit/:id',
          element:
            userRole === 'company' ? (
              <CompanyAdvertiseForm />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'company-ads-performance',
          element:
            userRole === 'company' ? (
              <CompanyAdsPerformance />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'company-ads-performance/details/:userId',
          element:
            userRole === 'company' ? (
              <CompanyAdsPerformanceDetails />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'license-renewal',
          element:
            userRole === 'company' ? (
              <LicenseRenewal />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'credit-management',
          element:
            userRole === 'company' ? (
              <CreditManagement />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'purchase-credits',
          element:
            userRole === 'company' ? (
              <PurchaseCredits />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'credit-history/purchase',
          element:
            userRole === 'company' || userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3' ? (
              <PurchaseHistory />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'credit-history/allocation',
          element:
            userRole === 'company' || userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3' ? (
              <AllocationHistory />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'credit-history/usage',
          element:
            userRole === 'company' || userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3' ? (
              <CreditUsageHistory />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'credit-history/expiration',
          element:
            userRole === 'company' || userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3' ? (
              <ExpirationHistory />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'history/account-type-conversion',
          element:
            userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3' ? (
              <AccountTypeConversionHistory />
            ) : (
              <Navigate to='/' />
            ),
        },
        {
          path: 'users',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'adminTier2']}>
              <Users />
            </ProtectedRouteWrapper>
          ),
        },
        // KPI routes
        {
          path: 'kpi/advertiser-performance',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <AdvertiserPerformance />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'wishlist-analyzer',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'adminTier2', 'adminTier3']}>
              <WishlistAnalyzer />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'kpi/advertiser-detail/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <AdvertiserDetail />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-category',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertyCategory />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-sub-category',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertySubCategory />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-sub-category/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertySubCategoryForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-sub-category/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertySubCategoryForm />
            </ProtectedRouteWrapper>
          ),
        },

        {
          path: 'property-sub-category/view/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertySubCategoryView />
            </ProtectedRouteWrapper>
          ),
        },

        {
          path: 'location-management',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <LocationManagement />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'location-management/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <LocationManagementForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'location-management/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <LocationManagementForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-feature',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertyFeature />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-feature/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertyFeatureForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-feature/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertyFeatureForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-facility',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertyFacility />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-facility/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertyFacilityForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'property-facility/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PropertyFacilityForm />
            </ProtectedRouteWrapper>
          ),
        },

        {
          path: 'advertise',
          element: (
            <ProtectedRouteWrapper
              ROLES={['admin', 'adminTier2', 'adminTier3']}
            >
              <Advertise />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'advertise/view/:id',
          element: (
            <ProtectedRouteWrapper
              ROLES={['admin', 'adminTier2', 'adminTier3']}
            >
              <AdvertiseView />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'advertise/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'adminTier2']}>
              <AdvertiseForm />
            </ProtectedRouteWrapper>
          ),
        },
        // {
        //   path: 'advertise/edit/:id',
        //   element: (
        //     <ProtectedRouteWrapper ROLES={['admin','adminTier2', 'adminTier3']}>
        //       <AdvertiseForm />
        //     </ProtectedRouteWrapper>
        //   ),
        // },
        {
          path: 'advertise-package',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <AdvertisePackage />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'settings',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <Settings />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'contact-form',
          element: (
            <ProtectedRouteWrapper ROLES={['admin','adminTier2']}>
              <ContactForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'payment',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <Payment />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'Notification',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <Notification />
            </ProtectedRouteWrapper>
          ),
        },

        {
          path: 'user/profile',
          element:
            userRole === 'company' ? (
              <Profile />
            ) : (
              <ProtectedRouteWrapper ROLES={['admin']}>
                <UserProfile />
              </ProtectedRouteWrapper>
            ),
        },
        { path: 'profile', element: <Profile /> },
      ],
    },
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { path: '/', element: <Navigate to='/' /> },

        {
          path: 'login',
          element: accessToken ? <Navigate to='/' /> : <Login />,
        },
        {
          path: 'company-login',
          element:
            accessToken && userRole === 'company' ? (
              <Navigate to='/company-dashboard' />
            ) : accessToken ? (
              <Navigate to='/' />
            ) : (
              <CompanyLogin />
            ),
        },
        {
          path: 'reset-password',
          element: <ResetPassword />,
        },
        // {
        //   path: 'verify-whatsapp-otp',
        //   element:
        //     accessToken && !WhatsappLoginOtpData?.requestId ? (
        //       <Navigate to='/login' />
        //     ) : (
        //       <VerifyWhatsappOtp />
        //     ),
        // },
      ],
    },

    // {
    //   path: '/',
    //   children: [
    //     {
    //       path: 'organizations',
    //       element:
    //         accessToken && userRole !== 'superAdmin' ? (
    //           <OrganizationList />
    //         ) : (
    //           <Navigate to='/login' />
    //         ),
    //     },
    //     { path: '404', element: <NotFound /> },
    //     { path: '*', element: <Navigate to='/404' /> },
    //   ],
    // },

    { path: '404', element: <NotFound /> },
    {
      path: '*',
      element: (
        <Navigate
          to='/404'
          replace
        />
      ),
    },
  ]);
};
export default Router;
